import { fluxoTermicoKWm2, raioParaFluxoM, type Ambiente, type BolaDeFogo } from '../bleve'
import type { IdFonte } from '../fontes'

/**
 * Zonas de trabalho a partir da radiação térmica de uma bola de fogo.
 *
 * ZQ (quente), ZM (morna) e ZF (fria) são a divisão operacional de uma cena
 * HAZMAT. O mapeamento abaixo é DIDÁTICO deste laboratório — mostra como um
 * limiar físico vira uma linha no chão — e não é doutrina:
 *
 * - ZQ: fluxo ≥ 5 kW/m² — queimadura de 2º grau em cerca de 40 s sem proteção
 *   (ponto final do EPA RMP). Enquanto houver risco de BLEVE, ninguém fica.
 * - ZM: 1,6 a 5 kW/m² — dor e queimadura em exposição prolongada.
 * - ZF: abaixo de 1,6 kW/m² — sem desconforto mesmo em exposição longa.
 *
 * O isolamento do GRE para tanque em fogo (1.600 m para GLP) é outra coisa:
 * é a distância para o PÚBLICO, por causa dos fragmentos, e não diminui
 * porque o cálculo de radiação deu menos.
 */

export type IdZona = 'quente' | 'morna' | 'fria'

export interface LimiarRadiacao {
  kWm2: number
  rotulo: string
  efeitoPessoa: string
  efeitoEdificacao: string
  fonte: IdFonte
}

export const LIMIARES_RADIACAO: readonly LimiarRadiacao[] = [
  {
    kWm2: 37.5,
    rotulo: 'Dano a equipamentos',
    efeitoPessoa: 'Letal em segundos (1% de letalidade em 10 s, 100% em 1 min).',
    efeitoEdificacao: 'Suficiente para danificar equipamentos de processo e estruturas metálicas.',
    fonte: 'bancoMundial1985',
  },
  {
    kWm2: 12.5,
    rotulo: 'Ignição de materiais',
    efeitoPessoa: 'Queimadura de 1º grau em 10 s; cerca de 1% de letalidade em 1 min.',
    efeitoEdificacao: 'Energia mínima para ignição pilotada de madeira e para derreter tubos plásticos.',
    fonte: 'bancoMundial1985',
  },
  {
    kWm2: 5,
    rotulo: 'Queimadura de 2º grau',
    efeitoPessoa: 'Queimadura de 2º grau em cerca de 40 s de exposição sem proteção.',
    efeitoEdificacao: 'Não inflama materiais comuns; vidros podem trincar em exposição longa.',
    fonte: 'epaRmp',
  },
  {
    kWm2: 1.6,
    rotulo: 'Sem desconforto',
    efeitoPessoa: 'Não causa desconforto, mesmo em exposição longa.',
    efeitoEdificacao: 'Nenhum efeito.',
    fonte: 'bancoMundial1985',
  },
]

/** Fluxo que separa ZQ de ZM e ZM de ZF, kW/m². */
export const LIMITE_QUENTE_KWM2 = 5
export const LIMITE_MORNA_KWM2 = 1.6

export const NOMES_ZONA: Record<IdZona, { sigla: string; nome: string; uso: string }> = {
  quente: {
    sigla: 'ZQ',
    nome: 'Zona quente',
    uso: 'Só entrada essencial, com proteção, pelo menor tempo. Com tanque exposto ao fogo, ninguém permanece.',
  },
  morna: {
    sigla: 'ZM',
    nome: 'Zona morna',
    uso: 'Apoio à zona quente e corredor de descontaminação; sem permanência do público.',
  },
  fria: {
    sigla: 'ZF',
    nome: 'Zona fria',
    uso: 'Posto de comando, área de espera de viaturas e de concentração de vítimas.',
  },
}

export interface AnelRadiacao {
  limiar: LimiarRadiacao
  /** `null` quando o fluxo nunca chega ao limiar no solo. */
  raioM: number | null
}

export interface ZonasRadiacao {
  aneis: AnelRadiacao[]
  /** Raio no solo coberto pela própria bola (metade do diâmetro). */
  envolvimentoM: number
  /** Raio externo da ZQ. */
  raioQuenteM: number
  /** Raio externo da ZM. */
  raioMornaM: number
  /** Isolamento do GRE para tanque em fogo (público), quando informado. */
  isolamentoGreM: number | null
}

export function zonasPorRadiacao(bola: BolaDeFogo, ambiente: Ambiente, isolamentoGreM: number | null = null): ZonasRadiacao {
  const aneis = LIMIARES_RADIACAO.map((limiar) => ({ limiar, raioM: raioParaFluxoM(bola, limiar.kWm2, ambiente) }))
  const envolvimentoM = bola.diametroM / 2
  const raioQuenteM = Math.max(envolvimentoM, raioParaFluxoM(bola, LIMITE_QUENTE_KWM2, ambiente) ?? 0)
  const raioMornaM = Math.max(raioQuenteM, raioParaFluxoM(bola, LIMITE_MORNA_KWM2, ambiente) ?? 0)
  return { aneis, envolvimentoM, raioQuenteM, raioMornaM, isolamentoGreM }
}

export function zonaNaDistancia(zonas: ZonasRadiacao, distanciaM: number): IdZona {
  if (distanciaM <= zonas.raioQuenteM) return 'quente'
  if (distanciaM <= zonas.raioMornaM) return 'morna'
  return 'fria'
}

export interface LeituraNoPonto {
  distanciaM: number
  fluxoKWm2: number
  zona: IdZona
  /** O limiar mais alto que o ponto atinge, para dizer o que acontece ali. */
  limiarAtingido: LimiarRadiacao | null
  dentroDaBola: boolean
  dentroDoIsolamentoGre: boolean
}

export function leituraNoPonto(bola: BolaDeFogo, ambiente: Ambiente, zonas: ZonasRadiacao, distanciaM: number): LeituraNoPonto {
  const fluxoKWm2 = fluxoTermicoKWm2(bola, distanciaM, ambiente)
  return {
    distanciaM,
    fluxoKWm2,
    zona: zonaNaDistancia(zonas, distanciaM),
    limiarAtingido: LIMIARES_RADIACAO.find((l) => fluxoKWm2 >= l.kWm2) ?? null,
    dentroDaBola: distanciaM <= zonas.envolvimentoM,
    dentroDoIsolamentoGre: zonas.isolamentoGreM != null && distanciaM <= zonas.isolamentoGreM,
  }
}
